/**
 * AuthGuard Component
 *
 * Protected route wrapper that redirects unauthenticated users to landing page.
 * Shows loading screen while checking auth state.
 */

import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { brand, fontSize, fontWeight, spacing } from '../../styles/theme';

export interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { state } = useAuth();

  // Show loading screen while checking auth state
  if (state.isLoading) {
    return <LoadingScreen />;
  }

  // Redirect to landing if not authenticated
  if (!state.user) {
    return <Navigate to="/" replace />;
  }

  // Show verification prompt if email not verified
  if (!state.emailVerified) {
    return (
      <div>
        {children}
        <EmailVerificationBanner />
      </div>
    );
  }

  // User is authenticated and verified
  return <>{children}</>;
}

/**
 * Loading screen shown while checking authentication state
 */
function LoadingScreen() {
  return (
    <div style={styles.loadingContainer}>
      <div style={styles.loadingContent}>
        {/* Logo (use text for now, can be replaced with actual logo) */}
        <div style={styles.logo}>Ostinato Labs</div>

        {/* Spinner */}
        <div style={styles.spinner}>
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={styles.spinnerSvg}
          >
            <circle
              cx="24"
              cy="24"
              r="20"
              stroke={brand.sage}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="80 100"
            />
          </svg>
        </div>

        <p style={styles.loadingText}>Loading...</p>
      </div>
    </div>
  );
}

/**
 * Banner shown when email is not verified
 */
function EmailVerificationBanner() {
  const { sendVerificationEmail } = useAuth();

  const handleResendEmail = async () => {
    try {
      await sendVerificationEmail();
      alert('Verification email sent! Check your inbox.');
    } catch (error) {
      alert('Failed to send verification email. Please try again.');
    }
  };

  return (
    <div style={styles.banner}>
      <div style={styles.bannerContent}>
        <p style={styles.bannerText}>
          Please verify your email to unlock all features
        </p>
        <button style={styles.bannerButton} onClick={handleResendEmail} type="button">
          Resend Email
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loadingContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: brand.charcoal,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'fadeIn 0.3s ease-out',
  },
  loadingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing.lg,
  },
  logo: {
    fontSize: fontSize.xxl,
    fontWeight: fontWeight.bold,
    color: brand.sage,
    letterSpacing: '0.05em',
  },
  spinner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinnerSvg: {
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: fontSize.md,
    color: brand.creamMuted,
    margin: 0,
  },
  banner: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: brand.warningBg,
    borderBottom: `1px solid ${brand.warning}`,
    zIndex: 200,
    padding: spacing.sm,
  },
  bannerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    flexWrap: 'wrap',
  },
  bannerText: {
    fontSize: fontSize.sm,
    color: brand.warning,
    margin: 0,
  },
  bannerButton: {
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
    backgroundColor: brand.warning,
    color: brand.charcoalDark,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
};

// Inject spin animation
const injectSpinAnimation = () => {
  const styleId = 'auth-guard-animations';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  `;
  document.head.appendChild(style);
};

// Inject animations immediately
injectSpinAnimation();

export default AuthGuard;
